"use client"

import React from 'react';
import { fabric } from 'fabric';

import Button from './Button';

import __ from './../utils/translation';

import { ReactComponent as IconFlipH } from './../icons/flip-h.svg';
import { ReactComponent as IconFlipV } from './../icons/flip-v.svg';
import { ReactComponent as IconBringFwd } from './../icons/bring-forward.svg';
import { ReactComponent as IconBringBack } from './../icons/bring-back.svg';
import { ReactComponent as IconDuplicate } from './../icons/duplicate.svg';
import { ReactComponent as IconRemove } from './../icons/bin.svg';
import { ReactComponent as IconGroup } from './../icons/group.svg';
import { ReactComponent as IconUngroup } from './../icons/ungroup.svg';

import { Card, CardContent } from './ui/card';
import { Label } from "./ui/label"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import { BringToFrontIcon, CopyIcon, FlipHorizontalIcon, FlipVerticalIcon, GroupIcon, LockIcon, RulerIcon, ScissorsIcon, SendToBackIcon, SquaresUnite, TrashIcon, UngroupIcon, UnlockIcon } from 'lucide-react';

const SelectionObjectSettings = ({ canvas, activeSelection }) => {

  const handleFlipH = () => {
    activeSelection.set('flipX', !activeSelection.flipX)
    canvas.renderAll()
    canvas.trigger('object:modified')
  }

  const handleFlipV = () => {
    activeSelection.set('flipY', !activeSelection.flipY)
    canvas.renderAll()
    canvas.trigger('object:modified')
  }

  const handleBringFwd = () => {
    canvas.bringForward(activeSelection)
    canvas.renderAll()
    canvas.trigger('object:modified')
  }

  const handleBringBack = () => {
    canvas.sendBackwards(activeSelection)
    canvas.renderAll()
    canvas.trigger('object:modified')
  }

  const handleDuplicate = () => {
    let clonedObjects = []
    let activeObjects = canvas.getActiveObjects()
    activeObjects.forEach(obj => {
      obj.clone(clone => {
        canvas.add(clone.set({
          strokeUniform: true,
          left: obj.aCoords.tl.x + 20,
          top: obj.aCoords.tl.y + 20
        }));

        if (activeObjects.length === 1) {
          canvas.setActiveObject(clone)
        }
        clonedObjects.push(clone)
      })
    })

    if (clonedObjects.length > 1) {
      let sel = new fabric.ActiveSelection(clonedObjects, {
        canvas: canvas,
      });
      canvas.setActiveObject(sel)
    }

    canvas.requestRenderAll()
    canvas.trigger('object:modified')
  }

  const handleDel = () => {
    canvas.getActiveObjects().forEach(obj => {
      canvas.remove(obj)
    })
    canvas.discardActiveObject().requestRenderAll()
    canvas.trigger('object:modified')
  }

  const handleGroup = () => {
    if (activeSelection.type !== 'activeSelection') return

    var isFixed = false;

    canvas.getActiveObjects().forEach(obj => {
      if (obj.default)
        isFixed = true;
    })

    canvas.getActiveObject().toGroup()

    canvas.requestRenderAll()
    canvas.trigger('object:modified')
  }

  const handleUngroup = () => {
    const active = canvas.getActiveObject();
    if (!active || active.type !== 'group') return;

    const group = active;
    const objects = group._objects;

    canvas.remove(group);

    objects.forEach(obj => {
      obj.left = group.left + obj.left * group.scaleX;
      obj.top = group.top + obj.top * group.scaleY;
      obj.scaleX *= group.scaleX;
      obj.scaleY *= group.scaleY;
      obj.angle += group.angle;
      obj.originX = 'left';
      obj.originY = 'top';
      obj.setCoords();
      canvas.add(obj);
    });

    const sel = new fabric.ActiveSelection(objects, { canvas });
    canvas.setActiveObject(sel);

    canvas.requestRenderAll();
    canvas.trigger('object:modified');
  };

  return (
    <TooltipProvider>
      <Card className="border-0 shadow-none bg-background p-3">
        <CardContent className="grid grid-cols-1 p-0">
          {/* DIM */}
          <div className="space-y-3 m-1">
            <div
              className="flex items-center justify-between rounded-lg bg-background p-3 transition-colors hover:bg-muted cursor-pointer shadow-xl"
            >
              <Label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                <RulerIcon></RulerIcon>
                <span className="" id="dim-final">Dimensão: </span>
              </Label>
            </div>
          </div>
        </CardContent>
        <CardContent className="p-0">
          <div className="grid grid-cols-3 gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    title="VIRAR HORIZONTALMENTE"
                    name="flip-h"
                    handleClick={() => handleFlipH()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <FlipHorizontalIcon></FlipHorizontalIcon>
                  </Button>
                  Virar Horizontal
                </div>
              </TooltipTrigger>
              <TooltipContent>Virar Horizontalmente</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    title="VIRAR VERTICALMENTE"
                    name="flip-v"
                    handleClick={() => handleFlipV()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <FlipVerticalIcon></FlipVerticalIcon>
                  </Button>
                  Virar Vertical
                </div>
              </TooltipTrigger>
              <TooltipContent>Virar Verticalmente</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    title="DUPLICAR"
                    name="duplicate"
                    handleClick={() => handleDuplicate()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <CopyIcon></CopyIcon>
                  </Button>
                  Duplicar
                </div>
              </TooltipTrigger>
              <TooltipContent>Duplicar</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    title="ENVIAR PARA FRENTE"
                    name="bring-fwd"
                    handleClick={() => handleBringFwd()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <BringToFrontIcon></BringToFrontIcon>
                  </Button>
                  Enviar Frente
                </div>
              </TooltipTrigger>
              <TooltipContent>Enviar para Frente</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    title="ENVIAR PARA TRÁS"
                    name="bring-back"
                    handleClick={() => handleBringBack()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <SendToBackIcon></SendToBackIcon>
                  </Button>
                  Enviar Trás
                </div>
              </TooltipTrigger>
              <TooltipContent>Enviar para Trás</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    title="REMOVER"
                    name="remove"
                    handleClick={() => handleDel()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-destructive/10 hover:text-destructive transition-colors items-center justify-center flex mb-1"
                  >
                    <TrashIcon></TrashIcon>
                  </Button>
                  Remover
                </div>
              </TooltipTrigger>
              <TooltipContent>Remover</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center  justify-center text-center text-xs">
                  <Button
                    title="AGRUPAR"
                    name="group"
                    handleClick={() => handleGroup()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <GroupIcon></GroupIcon>
                  </Button>
                  Agrupar
                </div>
              </TooltipTrigger>
              <TooltipContent>Agrupar</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center  justify-center text-center text-xs">
                  <Button
                    title="DESAGRUPAR"
                    name="ungroup"
                    handleClick={() => handleUngroup()}
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <UngroupIcon></UngroupIcon>
                  </Button>
                  Desagrupar
                </div>
              </TooltipTrigger>
              <TooltipContent>Desagrupar</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    id="crop"
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <ScissorsIcon></ScissorsIcon>
                  </Button>
                  Cortar
                </div>
              </TooltipTrigger>
              <TooltipContent>Cortar</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    id="lock_svg"
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <LockIcon id="obj-locked" className='hidden'></LockIcon>
                    <UnlockIcon id="obj-unlocked"></UnlockIcon>
                  </Button>
                  Cadeado
                </div>
              </TooltipTrigger>
              <TooltipContent>Trancar</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center justify-center text-center text-xs">
                  <Button
                    id="vectorize"
                    className="h-10 w-10 rounded-lg border bg-card hover:bg-accent transition-colors flex items-center justify-center mb-1"
                  >
                    <SquaresUnite></SquaresUnite>
                  </Button>
                  Vetorizar
                </div>
              </TooltipTrigger>
              <TooltipContent>Vetorizar img</TooltipContent>
            </Tooltip>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}

export default SelectionObjectSettings
